import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material'
import { ArrowForward as ArrowForwardIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import { clientsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'

interface TaskCompletionAdvanceDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  clientName: string
  currentStageLabel: string
  nextStageLabel: string
  onAdvanced: () => void
}

export default function TaskCompletionAdvanceDialog({
  open,
  onClose,
  clientId,
  clientName,
  currentStageLabel,
  nextStageLabel,
  onAdvanced,
}: TaskCompletionAdvanceDialogProps) {
  const { showSuccess, showError } = useNotification()
  const [advancing, setAdvancing] = useState(false)

  const handleAdvance = async () => {
    try {
      setAdvancing(true)
      await clientsApi.advanceWorkflowStage(clientId, undefined as any)
      showSuccess(`${clientName} advanced to ${nextStageLabel}`)
      onAdvanced()
      onClose()
    } catch (err) {
      console.error('Failed to advance stage:', err)
      showError('Failed to advance stage')
    } finally {
      setAdvancing(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          All Required Tasks Complete
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
          All required tasks for <strong>{currentStageLabel}</strong> are now complete.
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Would you like to advance <strong>{clientName}</strong> to <strong>{nextStageLabel}</strong>?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {currentStageLabel}
          </Typography>
          <ArrowForwardIcon sx={{ color: 'success.main' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {nextStageLabel}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={advancing}>
          Not Yet
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleAdvance}
          disabled={advancing}
          startIcon={advancing ? <CircularProgress size={16} /> : <ArrowForwardIcon />}
        >
          {advancing ? 'Advancing...' : 'Advance Stage'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
